import React from 'react';
import { Box, Container, Grid, Typography, Link, IconButton, Stack, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import TelelineLogo from '../../assets/images/teleline-logo-light.svg';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const Footer = () => {
  const { t } = useTranslation();

  const quickLinks = [
    { label: t('navigation.services'), path: '/services' },
    { label: t('navigation.caseStudies'), path: '/case-studies' },
    { label: t('navigation.about'), path: '/about' },
    { label: t('navigation.contact'), path: '/contact' },
  ];

  const socialLinks = [
    { icon: FacebookIcon, label: 'Facebook', href: '#' },
    { icon: LinkedInIcon, label: 'LinkedIn', href: '#' },
    { icon: TwitterIcon, label: 'Twitter', href: '#' },
  ];
  
  return (
    <Box
      component="footer"
      sx={{
        background: 'var(--gradient-dark)',
        color: 'var(--text-light)',
        pt: { xs: 6, md: 8 },
        pb: 4,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 80% 20%, rgba(37, 99, 235, 0.05) 0%, transparent 40%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Main Footer Content */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Brand Section */}
          <Grid item xs={12} sm={6} md={3}>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <Box sx={{ mb: 2 }}>
                <img 
                  src={TelelineLogo} 
                  alt="Teleline Logo" 
                  style={{
                    height: '35px',
                    width: 'auto',
                    display: 'block',
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: 1.6,
                  mb: 2,
                  fontSize: '0.9rem',
                }}
              >
                Teleline Information Services Ltd. - Innovative technology solutions for modern businesses.
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {socialLinks.map((social, index) => (
                  <motion.div
                    key={social.label}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <IconButton
                      component="a"
                      href={social.href}
                      aria-label={social.label}
                      size="small"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        transition: 'all var(--transition-base)',
                        '&:hover': {
                          color: 'var(--color-accent)',
                          backgroundColor: 'rgba(14, 165, 233, 0.1)',
                        },
                      }}
                    >
                      <social.icon fontSize="small" />
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>
            </MotionBox>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Quick Links
              </Typography>
              <Stack spacing={0.8}>
                {quickLinks.map((link) => (
                  <Link
                    key={link.path}
                    component={RouterLink}
                    to={link.path}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      transition: 'all var(--transition-fast)',
                      position: 'relative',
                      width: 'fit-content',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -2,
                        left: 0,
                        width: '0%',
                        height: '2px',
                        background: 'var(--color-accent)',
                        transition: 'width var(--transition-base)',
                      },
                      '&:hover': {
                        color: 'var(--color-accent)',
                        '&::after': {
                          width: '100%',
                        },
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </MotionBox>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6} md={6}>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Contact
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Email
                    </Typography>
                    <Link
                      href="mailto:info@teleline.co.il"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textDecoration: 'none',
                        fontSize: '0.85rem',
                        display: 'block',
                        transition: 'color var(--transition-fast)',
                        '&:hover': {
                          color: 'var(--color-accent)',
                        },
                      }}
                    >
                      info@teleline.co.il
                    </Link>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Phone
                    </Typography>
                    <Link
                      href="tel:+972-3-511-4482"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textDecoration: 'none',
                        fontSize: '0.85rem',
                        display: 'block',
                        transition: 'color var(--transition-fast)',
                        '&:hover': {
                          color: 'var(--color-accent)',
                        },
                      }}
                    >
                      03-511-4482
                    </Link>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Alt Phone
                    </Typography>
                    <Link
                      href="tel:+972-545-600-563"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        textDecoration: 'none',
                        fontSize: '0.85rem',
                        display: 'block',
                        transition: 'color var(--transition-fast)',
                        '&:hover': {
                          color: 'var(--color-accent)',
                        },
                      }}
                    >
                      0545-600-563
                    </Link>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Address
                    </Typography>
                    <Typography
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '0.85rem',
                      }}
                    >
                      Jabotinsky St 33/35, Ramat Gan
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </MotionBox>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.1)',
            my: 2,
          }}
        />

        {/* Bottom Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            fontSize: '0.85rem',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.8rem',
            }}
          >
            &copy; {new Date().getFullYear()} Teleline Information Services Ltd. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Link
              href="#"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: 'color var(--transition-fast)',
                '&:hover': {
                  color: 'var(--color-accent)',
                },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              href="#"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: 'color var(--transition-fast)',
                '&:hover': {
                  color: 'var(--color-accent)',
                },
              }}
            >
              Terms of Service
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
